import React, { useCallback, useRef, useState } from 'react'

interface DragReorderOptions {
  /**
   * Use the drag source's parent element as the drag image. Set this when the
   * draggable element is a small handle inside a larger row, so the user drags
   * a preview of the whole row rather than just the handle glyph.
   */
  dragImageFromParent?: boolean
}

export interface DragReorderHandlers {
  /** Index currently being dragged (for a "dragging" style), or null. */
  dragIndex: number | null
  /** Spread onto the draggable handle element for the row at `index`. */
  handleProps: (index: number) => {
    draggable: true
    onDragStart: (e: React.DragEvent) => void
    onDragEnd: (e: React.DragEvent) => void
  }
  /** Spread onto the row container for the row at `index`. */
  rowProps: (index: number) => {
    onDragOverCapture: (e: React.DragEvent) => void
    onDropCapture: (e: React.DragEvent) => void
  }
}

/**
 * Drag-to-reorder for a flat list. The draggable element is a handle inside
 * each row; the row observes dragover. `onReorder(from, to)` performs the move
 * (and any bookkeeping — parallel arrays, dirty flag) in the consumer's state.
 *
 * Uses midline-based insertion: the cursor must cross the target row's midline
 * to trigger a swap. Without this, swapping a small item with a tall one leaves
 * the cursor inside the same row after the swap, retriggering it on every
 * dragover (flicker).
 */
export function useDragReorder(
  onReorder: (from: number, to: number) => void,
  options: DragReorderOptions = {},
): DragReorderHandlers {
  const { dragImageFromParent = false } = options
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const dragIndexRef = useRef<number | null>(null)

  const onDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (dragImageFromParent) {
        const row = (e.currentTarget as HTMLElement).parentElement
        if (row) {
          const rect = row.getBoundingClientRect()
          e.dataTransfer.setDragImage(row, e.clientX - rect.left, e.clientY - rect.top)
        }
      }
      dragIndexRef.current = index
      setDragIndex(index)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', String(index))
    },
    [dragImageFromParent],
  )

  const onDragOver = useCallback(
    (e: React.DragEvent, overIndex: number) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      const from = dragIndexRef.current
      if (from === null) return

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const insertBelow = e.clientY > rect.top + rect.height / 2

      let target: number
      if (insertBelow) {
        target = from < overIndex ? overIndex : overIndex + 1
      } else {
        target = from < overIndex ? overIndex - 1 : overIndex
      }
      if (target === from) return

      onReorder(from, target)
      dragIndexRef.current = target
      setDragIndex(target)
    },
    [onReorder],
  )

  const onDragEnd = useCallback(() => {
    dragIndexRef.current = null
    setDragIndex(null)
  }, [])

  const handleProps = useCallback(
    (index: number) => ({
      draggable: true as const,
      onDragStart: (e: React.DragEvent) => onDragStart(e, index),
      onDragEnd,
    }),
    [onDragStart, onDragEnd],
  )

  const rowProps = useCallback(
    (index: number) => ({
      onDragOverCapture: (e: React.DragEvent) => onDragOver(e, index),
      onDropCapture: (e: React.DragEvent) => e.preventDefault(),
    }),
    [onDragOver],
  )

  return { dragIndex, handleProps, rowProps }
}
